import type { CableBoqItem } from "../../utils/fetchCables";
import type { SortCableRows, SortCableRowsArgs } from "./types";

/** Digits compare as NUMBERS, everything else as text: without this A121 sorts
 * above A32, which is the whole reason this file exists. */
const compareNaturally = (left: string, right: string): number => {
  const a = left.toUpperCase().split(/(\d+)/).filter(Boolean);
  const b = right.toUpperCase().split(/(\d+)/).filter(Boolean);
  for (let i = 0; i < Math.min(a.length, b.length); i += 1) {
    const one = a[i];
    const two = b[i];
    const bothNumeric = /^\d+$/.test(one) && /^\d+$/.test(two);
    const step = bothNumeric
      ? Number(one) - Number(two)
      : one.localeCompare(two);
    if (step !== 0) return step;
  }
  return a.length - b.length;
};

/** The id the ROW prints in its column, so the order he reads is the order he
 * sorted by. Null for a run that prints an em dash there. */
const leadIdOf = (
  item: CableBoqItem,
  { circuitKeys, circuitBreakers }: SortCableRowsArgs,
): string | null => {
  const circuitId = item.circuit;
  if (!circuitId) return null;
  return circuitKeys?.[circuitId] || circuitBreakers?.[circuitId] || null;
};

export const sortCableRows: SortCableRows = (args) => {
  const { items } = args;
  return [...items].sort((left, right) => {
    const leftId = leadIdOf(left, args);
    const rightId = leadIdOf(right, args);
    // A run with no id printed sits behind every run that has one: it is not
    // part of the sequence he is reading down, and interleaving it by its
    // symbol name would break the run of numbers in half.
    if (!leftId !== !rightId) return leftId ? -1 : 1;
    if (leftId && rightId) {
      const byId = compareNaturally(leftId, rightId);
      if (byId !== 0) return byId;
    }
    // Two runs under one id are two boards' runs; then the name, so the
    // unkeyed tail reads alphabetically rather than in extraction order.
    const byBoard = compareNaturally(left.board ?? "", right.board ?? "");
    if (byBoard !== 0) return byBoard;
    const byName = compareNaturally(left.text ?? "", right.text ?? "");
    if (byName !== 0) return byName;
    // Every tie broken down to the circuit id: the order must not depend on
    // whether the engine's sort happens to be stable.
    return (left.circuit ?? "").localeCompare(right.circuit ?? "");
  });
};
